import * as React from "react";
import { useEffect } from "react";
import { cn } from "../../../lib/cn";
import { Box, WithReset } from "../../Layout/BoxReset";
import HStack from "../../Layout/HStack";
import VStack from "../../Layout/VStack";
import { Text } from "../Text";

const SudoRadioButtons = React.forwardRef(
  /**
   * @param {{
   *   value: string,
   *   onChange: (value: string) => void,
   *   disabled?: boolean,
   *   options: Array<{
   *     value: string,
   *     label: string,
   *     description?: string,
   *   }>,
   *   orientation?: "horizontal" | "vertical",
   *   size?: "sm" | "default" | "lg",
   *   className?: string,
   *   spacing?: number,
   *   align?: "stretch" | "flex-start" | "flex-end" | "center" | "baseline",
   *   justify?: "flex-start" | "flex-end" | "center" | "space-between" | "space-around" | "space-evenly",
   *   wrap?: boolean,
   *   noBackground?: boolean,
   *   ref: React.Ref<HTMLDivElement>,
   * }} props
   * @param {React.Ref<HTMLDivElement>} ref - Forwarded ref
   * @returns {JSX.Element}
   */
  ({ className, orientation = "vertical", size = "default", options, value, onChange, disabled, spacing = 2, align, justify, wrap, noBackground = false, ...props }, ref) => {
    const Container = orientation === "horizontal" ? HStack : VStack;
    const selected = options.find((option) => option.value === value);

    const selectedIndex = options.findIndex((option) => option.value === value);

    useEffect(() => {
      const handleKeyDown = (event) => {
        // Do not intercept keyboard events if user is typing in an input/textarea
        const target = event.target;
        if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
          return;
        }

        const index = selectedIndex;
        if (disabled) return;
        const isHorizontal = orientation === "horizontal";
        let newIndex;
        switch (event.key) {
          case "ArrowRight":
            if (!isHorizontal) return;
            newIndex = (index + 1) % options.length;
            break;
          case "ArrowLeft":
            if (!isHorizontal) return;
            newIndex = (index - 1 + options.length) % options.length;
            break;
          case "ArrowDown":
            if (isHorizontal) return;
            newIndex = (index + 1) % options.length;
            break;
          case "ArrowUp":
            if (isHorizontal) return;
            newIndex = (index - 1 + options.length) % options.length;
            break;
          case "Home":
            newIndex = 0;
            break;
          case "End":
            newIndex = options.length - 1;
            break;
          case " ":
          case "Enter":
            event.preventDefault();
            if (onChange && options[index]) onChange(options[index].value);
            return;
          default:
            return;
        }
        event.preventDefault();
        const newValue = options[newIndex]?.value;
        if (newValue === undefined) return;
        if (onChange) onChange(newValue);
      };

      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [options, selectedIndex, disabled, orientation, onChange]);

    const sizeStyles = {
      sm: "h-3 w-3",
      default: "h-4 w-4",
      lg: "h-5 w-5",
    };

    const labelSizes = {
      sm: "text-sm",
      default: "text-base",
      lg: "text-lg",
    };

    const handleClick = (option) => {
      if (!disabled && onChange) {
        onChange(option.value);
      }
    };

    return (
      <WithReset>
        <Container
          ref={ref}
          spacing={spacing}
          align={align}
          justify={justify}
          wrap={wrap}
          className={cn("w-full focus-visible:outline-none focus-visible:outline-offset-[10px] focus-visible:outline-[var(--grey-2)] focus-visible:outline-1 focus-visible:rounded-[8px]", className)}
          role="radiogroup"
          aria-orientation={orientation}
          aria-disabled={disabled || undefined}
          {...props}
          tabIndex={0}
        >
          {options.map((option) => (
            <Box
              as={"button"}
              key={option.value}
              onClick={() => handleClick(option)}
              role="radio"
              aria-checked={selected?.value === option.value}
              aria-disabled={disabled || undefined}
              aria-labelledby={option.label}
              disabled={disabled}
              className={cn(
                "flex w-full items-center px-2.5 duration-150 ease-in-out cursor-pointer rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary-cta-high-contrast",
                // Conditional padding: equal padding when no description, asymmetric when description exists
                option.description ? "pb-3 pt-2" : "py-2.5",
                noBackground ? "bg-transparent" : "bg-app-document-background",
                "!border !border-solid transition-all",
                selected?.value === option.value
                  ? "!border-app-primary-cta-high-contrast text-app-primary-cta-high-contrast cursor-default hover:cursor-default"
                  : cn(
                      "!border-app-grey-2 text-app-grey-0 hover:text-app-grey-0 hover:!border-app-grey-1 active:scale-[0.995] hover:scale-[1.005] cursor-pointer",
                      noBackground ? "hover:bg-transparent" : "hover:bg-app-grey-4",
                    ),
                disabled && (noBackground ? "opacity-50 cursor-not-allowed hover:bg-transparent" : "opacity-50 cursor-not-allowed hover:bg-app-document-background"),
              )}
            >
              <Box className="flex items-center gap-2 w-full">
                <Box
                  className={cn(
                    "rounded-full !border !border-solid flex-none items-center justify-center",
                    selected?.value === option.value ? "!border-app-primary-cta-high-contrast" : "!border-app-grey-2",
                    sizeStyles[size],
                  )}
                >
                  {selected?.value === option.value && (
                    <Box
                      className={cn(
                        "rounded-full bg-app-primary-cta-high-contrast animate-in zoom-in-0",
                        size === "sm" && "w-2 h-2 mt-[1px] ml-[1px]",
                        size === "default" && "w-2.5 h-2.5 mt-[2px] ml-[2px]",
                        size === "lg" && "!w-3 !h-3 mt-[3px] ml-[3px]",
                      )}
                    />
                  )}
                </Box>

                <Box className="flex flex-col">
                  <Box className={cn("text-left", labelSizes[size])}>
                    <Text
                      size="base"
                      weight="medium"
                      color={selected?.value === option.value ? "primary-cta-high-contrast" : "grey-0"}
                      font="inter"
                      lineHeight="tight"
                    >
                      {option.label}
                    </Text>
                  </Box>
                  {option.description && (
                    <Box className={`text-left font-inter ${selected?.value === option.value ? "text-app-primary-cta-high-contrast" : "text-[var(--grey-1)]"}`}>
                      <Text
                        size="xs"
                        lineHeight="normal"
                        weight="medium"
                        color={selected?.value === option.value ? "primary-cta-high-contrast" : "grey-1"}
                        font="inter"
                        className="!text-[13px]"
                      >
                        {option.description}
                      </Text>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Container>
      </WithReset>
    );
  }
);

SudoRadioButtons.displayName = "SudoRadioButtons";

export { SudoRadioButtons };
